<template>
  <div style="position: relative">
    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <section
      class="is-flex is-justify-content-space-between is-align-items-center"
    >
      <b-field class="my-0">
        <b-input
          v-model="filterText"
          placeholder="Type to search for tournaments ..."
          type="search"
          icon="search"
        ></b-input>
      </b-field>

      <b-field
        class="my-0"
        label="status"
        label-position="on-border"
        type="is-info"
      >
        <b-select v-model="filterStatus">
          <option value="NotEnded" key="NotEnded">Not Ended</option>
          <option value="Coming" key="Coming">Coming</option>
          <option value="Live" key="Live">Live</option>
          <option value="Ended" key="Ended">Ended</option>
          <option value="All" key="All">All</option>
        </b-select>
      </b-field>

      <b-field
        class="my-0"
        label="Privacy"
        label-position="on-border"
        type="is-info"
      >
        <b-select v-model="filterPrivacy">
          <option value="" key="privacyAll">All</option>
          <option value="Private" key="privacyPrivate">Private</option>
          <option value="Invite" key="privacyInvite">Invite</option>
          <option value="Paid" key="privacyPaid">Paid</option>
          <option value="Open" key="privacyOpen">Open</option>
          <option value="Password" key="privacyPassword">Password</option>
        </b-select>
      </b-field>

      <label class="checkbox is-danger">
        <input type="checkbox" v-model="filterMineOnly" />
        Mine only
      </label>

      <router-link to="/hall-of-fame">
        <a class="is-size-5 has-text-link"> Hall of Fame &gt;&gt; </a>
      </router-link>
    </section>

    <div class="is-flex mt-4" v-if="canCreateTournament()">
      <b-button
        class="mr-2"
        label="Create tournament"
        type="is-success"
        icon-left="plus"
        @click="createTournament"
      />
    </div>

    <b-modal
      v-model="isComponentModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      :can-cancel="false"
    >
      <template #default="props">
        <TournamentForm :tournament="formProps" @close="props.close" />
      </template>
    </b-modal>

    <div class="mt-2">
      <TournamentTable
        v-if="!isLoading"
        :tournaments="availableTournaments"
        :myTournaments="myTournaments"
      />
    </div>
  </div>
</template>

<script>
import TournamentTable from "@/components/tournaments/TournamentTable.vue";
import { TournamentsAPI } from "@/services/api";
import { firestore } from "@/services/firebase";
import { mapGetters } from "vuex";
import TournamentPermission from "@/components/admins/TournamentPermission";

export default {
  name: "Tournaments",

  title() {
    return "Tournaments";
  },

  components: {
    TournamentTable,
  },

  mixins: [TournamentPermission],

  data() {
    return {
      sqlTournaments: [],
      firestoreTournaments: [],
      mySqlTournaments: [],
      filterText: "",
      filterStatus: "NotEnded",
      filterPrivacy: "",
      filterMineOnly: false,
    };
  },

  firestore: {
    firestoreTournaments: firestore
      .collection("tournaments")
      .where("isDeleted", "==", false),
  },

  computed: {
    ...mapGetters(["getUserId"]),
    ...mapGetters({
      currentPlayer: "getPlayer",
    }),

    isLoading() {
      return (
        !this.tournaments || !this.tournaments.length || !this.currentPlayer
      );
    },

    tournaments() {
      if (!this.currentPlayer) return [];

      return [
        ...this.firestoreTournaments
          .filter(
            (t) =>
              t.privacy !== "Private" ||
              t.ownerId === this.currentPlayer.user_id ||
              (this.currentPlayer &&
                t.playerUsernames &&
                t.playerUsernames.includes(this.currentPlayer.username))
          )
          .sort((a, b) => b.startTime - a.startTime),
        ...this.sqlTournaments,
      ];
    },

    myTournaments() {
      return [
        ...this.firestoreTournaments.filter(
          (t) =>
            t.playerUsernames &&
            t.playerUsernames.includes(this.currentPlayer.username)
        ),
        ...this.mySqlTournaments,
      ];
    },

    availableTournaments() {
      if (!this.tournaments) return [];
      let filtered = this.filterMineOnly
        ? this.myTournaments
        : this.tournaments;

      if (this.filterText) {
        filtered = filtered.filter((t) =>
          t.name.toLowerCase().includes(this.filterText.toLowerCase())
        );
      }

      if (this.filterPrivacy) {
        filtered = filtered.filter((t) => t.privacy === this.filterPrivacy);
      }

      switch (this.filterStatus) {
        case "NotEnded":
          filtered = filtered.filter((t) => !(t.currentRound > t.totalRounds));
          break;
        case "Coming":
          filtered = filtered.filter((t) => t.currentRound === 0);
          break;
        case "Live":
          filtered = filtered.filter(
            (t) => t.currentRound > 0 && t.currentRound < t.totalRounds
          );
          break;
        case "Ended":
          filtered = filtered.filter((t) => t.currentRound > t.totalRounds);
          break;
        default:
          break;
      }

      return filtered;
    },
  },

  watch: {
    getUserId: {
      immediate: true,
      handler() {
        if (!this.getUserId) return;
        this.getMyTournaments();
      },
    },
  },

  methods: {
    async getTournaments() {
      await TournamentsAPI.get().then((tournaments) => {
        this.sqlTournaments = tournaments;
      });
    },

    async getMyTournaments() {
      if (!this.getUserId) return;
      await TournamentsAPI.getUserTournaments(this.getUserId).then((data) => {
        this.mySqlTournaments = data;
      });
    },
  },

  created() {
    this.getTournaments();
  },
};
</script>

<style scoped></style>
